'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import {
  pickLocalized,
  useLocalized,
} from '~/(tenant)/_components/native/_lib/use-localized';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  DirectionalArrow,
  Eyebrow,
} from '~/(tenant)/_components/native/_primitives';
import Link from '~/(tenant)/_components/tenant-link';
import type { NumberedListSectionConfig } from '~/(tenant)/_lib/schema/native-home-config.schema';

import {
  type UpcomingFamilyBookings,
  flattenFamilyBookings,
  isAfterCutoff,
  upcomingCutoff,
  useNativeHomeUpcomingFamilyBookings,
} from './use-native-home-upcoming-family-bookings';

interface NumberedListSectionProps {
  config: NumberedListSectionConfig;
  accountId: string;
}

/**
 * Editorial alternative to quick-actions. Hairline-bordered list with
 * 01/02/03 numerals, serif title + sans description per item, terminal arrow.
 *
 * Used for: "To begin" onboarding, "Next steps" prompts, "Or explore"
 * tertiary actions.
 *
 * When `config.onlyIfNoBookings` is true, the section behaves as an
 * empty-state nudge: it only renders for visitors with no upcoming bookings
 * (signed-out users included) and disappears once they have one.
 */
export function NumberedListSection({
  config,
  accountId,
}: NumberedListSectionProps) {
  const t = useNativeTokens();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const shouldGate = !!config.onlyIfNoBookings;
  const { hide } = useHideWhenHasBookings(accountId, shouldGate);
  const eyebrow = useLocalized(config.eyebrow, config.eyebrow_ar);
  const title = useLocalized(config.title, config.title_ar);

  if (shouldGate && hide) return null;

  return (
    <section style={{ margin: '28px 22px 0' }}>
      {(eyebrow || title) && (
        <div style={{ marginBottom: 16 }}>
          {eyebrow && <Eyebrow style={{ marginBottom: 6 }}>{eyebrow}</Eyebrow>}
          {title && (
            <h2
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 24,
                fontWeight: 300,
                color: t.foreground,
                letterSpacing: '-0.01em',
                margin: 0,
              }}
            >
              {title}
            </h2>
          )}
        </div>
      )}

      {config.items.map((item, i) => {
        const itemTitle =
          pickLocalized(item.title, item.title_ar, locale) ?? item.title;
        const itemDescription = pickLocalized(
          item.description,
          item.description_ar,
          locale,
        );
        return (
          <Link
            key={i}
            href={item.href}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 18,
              padding: '20px 0',
              textAlign: 'start',
              textDecoration: 'none',
              color: t.foreground,
              borderTop: `1px solid ${t.line}`,
              borderBottom:
                i === config.items.length - 1 ? `1px solid ${t.line}` : 'none',
            }}
          >
            <span
              style={{
                fontFamily: t.fontMono,
                fontSize: 10,
                letterSpacing: '0.18em',
                color: t.muted,
                fontWeight: 500,
                flexShrink: 0,
                minWidth: 20,
              }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: t.fontDisplay,
                  fontSize: 19,
                  fontWeight: 400,
                  color: t.foreground,
                  lineHeight: 1.2,
                }}
              >
                {itemTitle}
              </div>
              {itemDescription && (
                <div
                  style={{
                    fontFamily: t.fontSans,
                    fontSize: 12,
                    color: t.muted,
                    marginTop: 4,
                    lineHeight: 1.4,
                  }}
                >
                  {itemDescription}
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0 }}>
              <DirectionalArrow
                size={14}
                strokeWidth={1.4}
                color={t.foreground}
              />
            </div>
          </Link>
        );
      })}
    </section>
  );
}

interface UpcomingBookingEntry {
  booking_date?: string | null;
  start_time?: string | null;
  booking_status?: string | null;
}

function selectHasUpcomingBooking({
  fetchedAt,
  accounts,
}: UpcomingFamilyBookings): boolean {
  const cutoff = upcomingCutoff(fetchedAt);

  return flattenFamilyBookings<UpcomingBookingEntry>(accounts).some(
    (b) =>
      ['confirmed', 'pending'].includes(b.booking_status ?? '') &&
      isAfterCutoff(b, cutoff),
  );
}

/**
 * Returns `hide: true` when the section should be hidden because the signed-in
 * user has at least one upcoming booking. Signed-out users are never hidden
 * (they have no bookings, and this is exactly the moment to nudge them).
 *
 * Mirrors the cutoff logic from MyBookingsSection so the two sections agree
 * on what "upcoming" means.
 */
function useHideWhenHasBookings(accountId: string, enabled: boolean) {
  const client = useSupabase();

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
    enabled,
  });

  const userId = session?.user?.id;

  const { data: hasBookings } = useNativeHomeUpcomingFamilyBookings({
    userId,
    accountId,
    enabled,
    select: selectHasUpcomingBooking,
  });

  return { hide: hasBookings === true };
}
